import React, { useEffect, useRef, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowLeft, ArrowRight, Calendar, User, Tag } from 'lucide-react';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';
import Breadcrumb from '../components/Breadcrumb';
import { useSEO } from '../hooks/useSEO';
import { BLOG_POSTS, getBlogPost, formatDate } from '../data/blogPosts';
import { fieldNotes } from '../data/fieldNotes';

const PROSE_AURA_STYLES = `
.prose-aura {
  font-family: var(--font-serif);
  font-size: 1.125rem;
  line-height: 1.9;
  color: rgba(255,255,255,0.75);
  max-width: 680px;
  margin-left: auto;
  margin-right: auto;
}
@media (min-width: 768px) {
  .prose-aura { font-size: 1.1875rem; }
}
.prose-aura p {
  margin-bottom: 2em;
}
.prose-aura h2 {
  font-family: var(--font-display);
  font-size: 2rem;
  font-weight: 800;
  color: #FFFFFF;
  letter-spacing: -0.03em;
  margin-top: 2.5em;
  margin-bottom: 0.8em;
  line-height: 1.15;
}
.prose-aura h3 {
  font-family: var(--font-display);
  font-size: 1.25rem;
  font-weight: 700;
  color: rgba(255,255,255,0.9);
  letter-spacing: -0.01em;
  margin-top: 2.5em;
  margin-bottom: 0.75em;
}
.prose-aura hr {
  display: none;
}
.prose-aura ul {
  padding-left: 0;
  margin: 1.75em 0;
  list-style-type: none;
}
.prose-aura ul li {
  position: relative;
  padding-left: 1.5em;
  margin-bottom: 1.1em;
  line-height: 1.8;
}
.prose-aura ul li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0.7em;
  width: 6px;
  height: 1px;
  background: #D4AF37;
  opacity: 0.5;
}
.prose-aura ol {
  padding-left: 1.4em;
  margin: 1.75em 0;
  counter-reset: list;
  list-style-type: none;
}
.prose-aura ol li {
  counter-increment: list;
  position: relative;
  padding-left: 1.2em;
  margin-bottom: 1.1em;
  line-height: 1.8;
}
.prose-aura ol li::before {
  content: counter(list) '.';
  position: absolute;
  left: -0.8em;
  color: #D4AF37;
  opacity: 0.5;
  font-family: var(--font-display);
  font-size: 0.85em;
}
.prose-aura strong {
  color: #FFFFFF;
  font-weight: 600;
}
.prose-aura em {
  font-style: italic;
  color: rgba(255,255,255,0.85);
}
.prose-aura a {
  color: #F0C75E;
  text-decoration: underline;
  text-underline-offset: 4px;
  text-decoration-thickness: 2px;
  text-decoration-color: #F0C75E;
  font-weight: 600;
  transition: color 0.2s, background 0.2s;
}
.prose-aura a:hover {
  color: #FFFFFF;
  background: rgba(240,199,94,0.15);
  border-radius: 2px;
}
.prose-aura blockquote {
  border-left: 3px solid #D4AF37;
  padding: 0 0 0 1.5em;
  margin: 2.5em 0;
  font-style: italic;
  font-size: 1.1875rem;
  line-height: 1.75;
  color: rgba(255,255,255,0.55);
}
.prose-aura blockquote p {
  margin-bottom: 0;
}
.prose-aura img {
  max-width: 100%;
  height: auto;
  border-radius: 0.25rem;
  margin: 2.5em 0;
}
.prose-aura p:last-child em:first-child {
  color: rgba(212,175,55,0.6);
  font-size: 0.9375rem;
}
.prose-aura .pull-quote {
  font-family: var(--font-serif);
  font-size: 1.5rem;
  line-height: 1.6;
  font-style: italic;
  text-align: center;
  padding: 2em 1em;
  margin: 2.5em 0;
  border-top: 1px solid rgba(197,160,89,0.2);
  border-bottom: 1px solid rgba(197,160,89,0.2);
  color: rgba(255,255,255,0.55);
}
@media (min-width: 768px) {
  .prose-aura .pull-quote {
    font-size: 1.625rem;
    padding: 2.5em 2em;
    margin: 3em -2em;
  }
}
`;

const JournalPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const post = slug ? getBlogPost(slug) : undefined;
  const contentRef = useRef<HTMLDivElement>(null);

  // Intercept internal link clicks in blog HTML to use React Router navigation
  const handleContentClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const anchor = target.closest('a');
    if (!anchor) return;
    const href = anchor.getAttribute('href');
    if (href && href.startsWith('/') && !href.startsWith('//')) {
      e.preventDefault();
      navigate(href);
    }
  }, [navigate]);

  // Redirect to journal index if post not found
  useEffect(() => {
    if (slug && !post) {
      navigate('/journal', { replace: true });
    }
  }, [slug, post, navigate]);

  useSEO(
    post
      ? {
          title: post.metaTitle,
          description: post.metaDescription,
          canonical: `https://www.auratraveler.com/journal/${post.slug}`,
          ogImage: post.featuredImage,
          breadcrumbs: [
            { name: 'Home', url: 'https://www.auratraveler.com/' },
            { name: 'Journal', url: 'https://www.auratraveler.com/journal' },
            { name: post.title, url: `https://www.auratraveler.com/journal/${post.slug}` },
          ],
        }
      : {
          title: 'The Aura Journal | Aura Traveler',
          description: 'Luxury travel insights from Aura Traveler.',
        }
  );

  if (!post) return null;

  const currentIndex = BLOG_POSTS.findIndex((p) => p.slug === slug);
  const prevPost = currentIndex > 0 ? BLOG_POSTS[currentIndex - 1] : null;
  const nextPost = currentIndex < BLOG_POSTS.length - 1 ? BLOG_POSTS[currentIndex + 1] : null;

  // BlogPosting schema — Person author + freshness/keyword signals for AEO
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.metaDescription,
    author: {
      '@type': 'Person',
      name: post.author,
      worksFor: { '@type': 'Organization', name: 'Aura Traveler' },
    },
    datePublished: post.date,
    dateModified: post.date,
    keywords: post.tags.join(', '),
    articleSection: post.category,
    inLanguage: 'en-US',
    image: post.featuredImage,
    publisher: {
      '@type': 'Organization',
      name: 'Aura Traveler',
      url: 'https://www.auratraveler.com',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.auratraveler.com/journal/${post.slug}`,
    },
  };

  return (
    <div className="min-h-screen bg-charcoal text-white font-sans selection:bg-gold/30 selection:text-white">
      <style dangerouslySetInnerHTML={{ __html: PROSE_AURA_STYLES }} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />

      <SiteNav />

      <main>
      {/* Hero Image */}
      <section className="pt-24 relative h-[60vh] min-h-[400px] overflow-hidden">
        <img
          src={post.featuredImage}
          alt={post.featuredImageAlt}
          loading="eager"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
          className="absolute inset-0 w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/40 via-charcoal/60 to-charcoal" />
        <div className="absolute bottom-0 left-0 right-0 px-4 sm:px-6 md:px-12 lg:px-16 pb-12" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.8), 0 1px 4px rgba(0,0,0,0.5)' }}>
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: 'easeOut' }}
            >
              <div className="flex flex-wrap items-center gap-4 mb-6">
                {post.tags.slice(0, 2).map((tag) => (
                  <span
                    key={tag}
                    className="font-sans text-[10px] uppercase tracking-[0.3em] text-gold/70 border border-gold/30 px-3 py-1"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="font-display text-3xl sm:text-5xl md:text-6xl text-white leading-tight tracking-tighter font-bold">
                {post.title}
              </h1>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Breadcrumb + Article Metadata */}
      <section className="px-4 sm:px-6 md:px-12 lg:px-16 py-8 border-b border-white/5">
        <div className="max-w-4xl mx-auto mb-6">
          <Breadcrumb items={[
            { label: 'Journal', href: '/journal' },
            { label: post.title },
          ]} />
        </div>
        <div className="max-w-4xl mx-auto flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-2 text-white/40">
            <User className="w-3.5 h-3.5" />
            <span className="font-sans text-[10px] uppercase tracking-[0.3em]">{post.author}</span>
          </div>
          <div className="flex items-center gap-2 text-white/40">
            <Calendar className="w-3.5 h-3.5" />
            <span className="font-serif text-[11px] italic">{formatDate(post.date)}</span>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="px-4 sm:px-6 md:px-12 lg:px-16 py-24">
        <div className="max-w-3xl mx-auto">
          {/* Excerpt / Lede */}
          <div className="max-w-[680px] mx-auto mb-16">
            <p className="font-serif text-xl md:text-[1.375rem] text-white/70 leading-[1.8] italic">
              {post.excerpt}
            </p>
            <div className="mt-12" />
          </div>

          {/* Body */}
          <div
            ref={contentRef}
            className="prose-aura"
            dangerouslySetInnerHTML={{ __html: post.content }}
            onClick={handleContentClick}
          />
        </div>
      </article>

      {/* Tags */}
      <section className="px-4 sm:px-6 md:px-12 lg:px-16 pb-16">
        <div className="max-w-3xl mx-auto">
          <div className="flex flex-wrap items-center gap-3 pt-8 border-t border-white/5">
            <Tag className="w-4 h-4 text-white/20" />
            {post.tags.map((tag) => (
              <span
                key={tag}
                className="font-sans text-[10px] uppercase tracking-[0.3em] text-white/40 border border-white/10 px-3 py-1.5 hover:border-gold/30 hover:text-gold/70 transition-all duration-300 cursor-default"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Post Navigation */}
      <section className="px-4 sm:px-6 md:px-12 lg:px-16 py-16 border-t border-white/5">
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          {prevPost ? (
            <Link
              to={`/journal/${prevPost.slug}`}
              className="group flex items-start gap-4 border border-white/5 p-6 hover:border-gold/20 transition-all duration-500 bg-[#141414]"
            >
              <ArrowLeft className="w-5 h-5 text-white/30 group-hover:text-gold flex-shrink-0 mt-1 transition-colors duration-300" />
              <div>
                <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-white/30 block mb-2">
                  Previous
                </span>
                <span className="font-display text-base text-white/70 group-hover:text-white transition-colors duration-300 leading-tight">
                  {prevPost.title}
                </span>
              </div>
            </Link>
          ) : (
            <div />
          )}
          {nextPost ? (
            <Link
              to={`/journal/${nextPost.slug}`}
              className="group flex items-start gap-4 border border-white/5 p-6 hover:border-gold/20 transition-all duration-500 bg-[#141414] md:text-right"
            >
              <div className="flex-grow">
                <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-white/30 block mb-2">
                  Next
                </span>
                <span className="font-display text-base text-white/70 group-hover:text-white transition-colors duration-300 leading-tight">
                  {nextPost.title}
                </span>
              </div>
              <ArrowRight className="w-5 h-5 text-white/30 group-hover:text-gold flex-shrink-0 mt-1 transition-colors duration-300" />
            </Link>
          ) : (
            <div />
          )}
        </div>
        <div className="max-w-4xl mx-auto mt-8 text-center">
          <Link
            to="/journal"
            className="font-sans text-[10px] uppercase tracking-[0.3em] text-white/40 hover:text-gold transition-colors duration-300"
          >
            &larr; Back to Journal
          </Link>
        </div>
      </section>

      {/* Related Field Notes */}
      <section className="py-20 px-4 sm:px-6 md:px-12 lg:px-16 bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-12">
            <div>
              <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold/50 mb-3 block">From the Field</span>
              <h2 className="font-display text-2xl md:text-3xl text-white tracking-tighter">
                Related Field Notes
              </h2>
            </div>
            <Link
              to="/field-notes"
              className="hidden md:flex items-center gap-2 font-sans text-[10px] uppercase tracking-[0.3em] text-gold/70 hover:text-gold transition-colors duration-300"
            >
              View All <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {fieldNotes.slice(0, 3).map((note) => (
              <Link key={note.slug} to={`/field-notes/${note.slug}`} className="group block">
                <div className="border border-white/5 hover:border-gold/20 transition-all duration-700 bg-[#141414]">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={note.heroImage}
                      alt={note.title}
                      loading="lazy"
                      onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement!.classList.add('bg-gradient-to-br', 'from-[#1a1a1a]', 'to-[#2a2a2a]'); }}
                      className="w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700"
                    />
                  </div>
                  <div className="p-6">
                    <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-gold/50 mb-3 block">{note.category}</span>
                    <h3 className="font-display text-base text-white tracking-tight group-hover:text-gold transition-colors duration-500 leading-tight mb-2">
                      {note.title}
                    </h3>
                    <p className="font-serif text-[12px] text-white/40 line-clamp-2">{note.excerpt}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Related CTA */}
      <section className="py-24 px-4 sm:px-6 md:px-12 lg:px-16 bg-charcoal border-t border-white/5 text-center">
        <h2 className="font-display text-3xl md:text-5xl text-white tracking-tighter mb-6">
          Experience Aura{' '}
          <span className="font-serif italic text-gold">firsthand</span>
        </h2>
        <p className="font-serif text-lg text-white/40 mb-10 max-w-xl mx-auto">
          Generate a magazine-quality itinerary for any destination in under 90 seconds.
        </p>
        <Link
          to="/"
          className="inline-block px-12 py-5 bg-gold text-black font-sans text-[10px] font-bold uppercase tracking-[0.3em] hover:bg-white transition-all duration-700 rounded-full"
        >
          Start Planning
        </Link>
      </section>

      </main>

      <SiteFooter />
    </div>
  );
};

export default JournalPostPage;
